<template>
  <div class="column store-select visible" >
    <div class="label row">
      <span v-if="isBuyTransaction">{{
        content.storeFinderSellLabel ||
        '3. Select a eurochange branch to collect your GBP from'
      }}</span>
      <span v-else>
        {{
          content.storeFinderLabel ||
          '4. Select a eurochange branch to collect your currency from'
        }}
      </span>
      <Tooltip :content="content.clickAndCollectTooltip" />
    </div>

    <div class="find-store" v-show="!store || store.isMeta">      
      <label id="googleAutocomplete">Enter your postcode, town or city</label>
        <input 
        id="GooglePlaces"
        class="form-control"
        ref="collectionPlaceAutocomplete"
        type="search"
        placeholder="eg: WC2N 5HY"
        />

      <button class="button button--secondary" @click="getLocationFromDevice()" v-if="!isEmbedded">
        <span>Find a branch near me</span>
      </button>

      <p v-if="!isEmbedded">
        Or browse with our <a href="/branches"><span>branch finder</span></a>
      </p>
    </div>
        
    <div class="selected" v-if="store && !store.isMeta">
      <div class="label row">
        <span>Selected Store</span>
        <button class="close" @click="reset()">Reset</button>
      </div>
      <p class="label row">
        {{ store.brandName }} {{ store.name }}
      </p>
    </div>
  </div>
</template>
<script>
  import axios from 'axios'

  import { mapActions, mapState, mapGetters } from 'vuex'
  import { GEOCODING_URL, GEOCODING_KEY } from '@vue/common/constants/config'
  import { getParameterByName } from '@vue/services/calculator.service'
  import Tooltip from '@vue/common/components/Tooltip'
  import { showError } from '@vue/services/toast.service'
  import { setOptions, importLibrary } from '@googlemaps/js-api-loader'

  const LOCATION_PERMISSION_DENIED = 1;

  const xtrisOrderTypes = {
    SellBack: "B",
    HomeDelivery: "D",
    PayAndCollect: "CP"
  };

  setOptions({ key: window.siteSettings.googleMapsApiKey }); 

  const getAddressComponent = (place, type) => {
    let component = place.address_components.filter(x => x.types.includes(type))[0]
    if (!component) {
      return null;
    }
    return component.long_name || null;
  };

  export default {
    name: 'StoreFinder',
    components: {
      Tooltip
    },
    inject: ['isEmbedded'],
    data() {
      return {
      }
    },
    computed: {
      ...mapState({
        content: (state) => state.calculator.content,
        store: (state) => state.store.store,
        selectedCurrency: (state) => state.calculator.selectedCurrency,
        quantity: (state) => state.calculator.quantity,
        selectedProduct: (state) => state.calculator.selectedProduct,
      }),
      ...mapGetters({
        isBuyTransaction: 'calculator/isBuyTransaction',
        isSellTransaction: 'calculator/isSellTransaction',
      })
    },
    async mounted() {
      const queryStore = getParameterByName('store_id')

      if (queryStore) {
        this.getStore(queryStore)
      }
      this.setStoreAddress();

      const autocompleteOptions = {
        componentRestrictions: { country: 'gb' },
        fields: ['address_components', 'geometry'],
        types: ['geocode']
      };

      let  places  = await importLibrary("places");

      const autocomplete = new places.Autocomplete(document.getElementById("GooglePlaces"), autocompleteOptions);
      
      autocomplete.addListener('place_changed', () => this.getLocationFromGooglePlacesResult(autocomplete.getPlace()));      
    },
    methods: {
      ...mapActions({
        getNearby: 'store/getNearby',
        toggleStoreModal: 'calculator/toggleStoreModal',
        clearStores: 'store/clearStores',
        getStore: 'store/getStore',
        removeStoreSelection: 'store/removeStoreSelection',
        updateQuote: 'calculator/updateQuote'
      }),
      async getLocationFromGooglePlacesResult(place) {
        let coords = {};
        if(place) {
          if(place.geometry) {
            // If Google Places returns lat and lng use these
            coords.lat = place.geometry.location?.lat();
            coords.lng = place.geometry.location?.lng();
          } else {
            let placeSearchString = "";

            // If Google Places doesn't return exact lat and lng
            // then feed what it does return into Addressy
            if(place.address_components) {
              address_components
              placeSearchString = `${getAddressComponent(place, 'route')}, ${getAddressComponent(place, 'locality')}, ${getAddressComponent(place, 'administrative_area_level_1')}, ${getAddressComponent(place, 'administrative_area_level_2')}`;
            } else if(place.name) {
              console.log(`place.name ${place.name}`);
              console.log(place);
              placeSearchString = place.name;
            };

            if(placeSearchString) {
              coords = await this.getLocationFromAddressy(placeSearchString);
            }
          }
        }

        if(coords.lat && coords.lng) {
          this.showListOfNearbyStores({...coords});
        }
        else {
          showError("Sorry, we could not find a location based on your search");
          this.$refs.collectionPlaceAutocomplete.focus();
          this.$refs.collectionPlaceAutocomplete.select();
        }
      },
      async getLocationFromAddressy(placeSearchString) {
        const params = {
          Key: encodeURIComponent(GEOCODING_KEY),
          Country: encodeURIComponent('GBR'),
          Location: encodeURIComponent(placeSearchString),
        };

        const queryString = Object.keys(params)
          .map((key) => key + '=' + params[key])
          .join('&');

        const url = `${GEOCODING_URL}?${queryString}`;

        const addressyResponse = await axios.post(url, null, { withCredentials: false });

        if(addressyResponse.data.Items[0]) {
          return {
            lat: addressyResponse.data.Items[0].Latitude,
            lng: addressyResponse.data.Items[0].Longitude
          }
        } else {
          return null;
        }
      },
      getLocationFromDevice() {
        const success = pos => this.showListOfNearbyStores({lat: pos.coords.latitude, lng: pos.coords.longitude});

        const error = ({ code }) => {
            showError(`Sorry we couldn't get your location. ${code === LOCATION_PERMISSION_DENIED ? "Please grant access when a dialog appears." : ""}`);
        }

        navigator.geolocation.getCurrentPosition(success, error);
      },
      showListOfNearbyStores({lat, lng}) {
        // For reasons unknown, the nearby branches endpoint requires an XTRIS order type passed.
        // To clean this up likely requires adding some v2 endpoints to the API.
        const nearbyEndpointTransactionType = this.isSellTransaction
        ? xtrisOrderTypes.HomeDelivery
        : xtrisOrderTypes.SellBack;

        this.getNearby({
          Latitude: lat,
          Longitude: lng,
          amount: this.quantity,
          currency: this.selectedCurrency.code,
          transactionType: nearbyEndpointTransactionType
        });
        this.toggleStoreModal(true)
      },
      async setStoreAddress() {
        // If we are on BranchPage setStore else skipping
        if (window.branchId) {
          await this.getStore(window.branchId);
          this.hideBranchFinder = true;
          this.disableToggleStore = true;
        }
      },
      enableToggleStoreModal() {
        if (!this.disableToggleStore) {
          this.toggleStoreModal(true);
        }
      },
      reset() {
        this.clearStores();
        this.removeStoreSelection();

        // Clear location field so that user clear no location is selected
        this.$refs.collectionPlaceAutocomplete.value = '';
        this.$refs.collectionPlaceAutocomplete.focus();

        // Because increment would change if switching from an Express store
        this.updateQuote(); 
      }
    },
  }
</script>
